import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  indiceMaisProximo,
  mover,
  moverUmaCasa,
  velocidadeDaRolagem
} from "./ordenacao";

// Quanto o ponteiro precisa andar para virar arrasto. Abaixo disso é clique — e
// o clique aqui abre o item para editar, então sem esse limiar todo clique
// levantaria um card.
const LIMIAR_DE_ARRASTO = 5;

// Quanto o card cresce enquanto está na mão. Junto com o anel azul, a sombra
// funda e o fundo azulado (ver `.ct-passo--arrastado`), é o que faz o professor
// não perder de vista qual card está movendo numa grade de 40 iguais. 6% é o
// bastante para o olho notar sem o card cobrir os vizinhos.
const ESCALA_NA_MAO = 1.06;

// A faixa junto às bordas da grade em que o arrasto passa a rolar, e a velocidade
// no limite dela. 48px é pouco mais que a metade de um card (109px de altura), o
// que deixa a zona alcançável sem invadir o meio da área; 14px por quadro dá
// ~840px por segundo a 60fps, o bastante para atravessar dez linhas em pouco mais
// de um segundo sem passar do ponto.
const ZONA_DE_ROLAGEM = 48;
const VELOCIDADE_DE_ROLAGEM = 14;

/**
 * Os passos da trilha, em grade, arrastáveis.
 *
 * Substitui a tira horizontal do `ClickAndDropActivity` NO MODO TRILHA. A tira
 * mostrava 4 de 9 itens por vez (1096px de conteúdo em 556px de coluna) e cortava
 * o nome em 82px úteis — "Verbos no pretérito perfeito" precisa de 217px. Aqui o
 * nome tem três linhas e a grade quebra sozinha: 40 passos rolam para baixo, que
 * é o lado em que a página do AVA já rola.
 *
 * O arrasto é próprio, por pointer events, e não com `react-beautiful-dnd`. A lib
 * está no projeto e funciona bem em fila, mas erra o índice numa grade que quebra
 * linha — o modelo dela é de um eixo só. Medido: 1 acerto em 8 arrastos.
 * `indiceMaisProximo` (em ./ordenacao) resolve por distância até o centro, o que
 * trata as duas dimensões igual.
 *
 * OS CARDS ABREM ESPAÇO ENQUANTO SE ARRASTA, e isso não é enfeite: sem ver a
 * fila ceder lugar, o professor solta o card sem saber onde ele vai cair — foi a
 * queixa de 06/08/2026 sobre a primeira versão, que só acendia uma borda no
 * destino. Quem se desloca são os cards ENTRE a origem e o destino, cada um para
 * a posição do vizinho, com as coordenadas reais medidas no início do gesto (é o
 * que faz a passagem da última coluna para a linha de baixo sair certa).
 *
 * Pointer events cobrem mouse e toque com o mesmo código, e é o que o projeto já
 * escolheu quando precisou de arrasto de verdade (`TrilhaLudica/nucleo/
 * navegacaoDaPista.js`).
 *
 * @param {Array}    itens       o `contentList` do ActivityCreator
 * @param {Array}    tipos       `state.types`, para achar cor e ícone de cada item
 * @param {Function} onReordenar recebe a lista nova, já na ordem
 * @param {Function} onEditar    (type, id) do item clicado
 * @param {Function} onRemover   (type, id) do item removido
 */
function PercursoDaTrilha({
  itens = [],
  tipos = [],
  onReordenar,
  onEditar,
  onRemover
}) {
  // `arrastando` e `alvo` existem em DOBRO — no estado, para o render deslocar os
  // cards, e em `ref`, para o gesto ler. Não é redundância: os ouvintes vivem no
  // documento e são registrados uma vez só, então o closure deles congelaria no
  // primeiro render. Foi o defeito da primeira versão: o `pointerup` lia
  // `alvo === null` e nada reordenava.
  const [arrastando, setArrastando] = useState(null);
  const [alvo, setAlvo] = useState(null);
  // Os retângulos medidos no início do gesto, no estado porque o RENDER precisa
  // deles para calcular quanto cada card se desloca.
  const [geometria, setGeometria] = useState(null);

  const alvoRef = useRef(null);
  const listaRef = useRef(null);
  const cardsRef = useRef([]);
  const gesto = useRef(null);
  const rolagem = useRef(null);
  // Os dados de fora precisam estar frescos para o mesmo closure.
  const dados = useRef({ itens, onReordenar });
  useEffect(() => {
    dados.current = { itens, onReordenar };
  });

  const tipoDe = useCallback(
    item => tipos.find(tipo => tipo.id === item.type) || {},
    [tipos]
  );

  const terminarGesto = useCallback(() => {
    // O card arrastado seguia o ponteiro por estilo inline, escrito direto no DOM
    // (ver `aoMover`): quem escreveu à mão precisa apagar à mão.
    const indice = gesto.current && gesto.current.indice;
    const card = cardsRef.current[indice];
    if (card) card.style.transform = "";

    if (rolagem.current) {
      cancelAnimationFrame(rolagem.current);
      rolagem.current = null;
    }

    gesto.current = null;
    alvoRef.current = null;
    setArrastando(null);
    setAlvo(null);
    setGeometria(null);
  }, []);

  const marcarAlvo = useCallback(indice => {
    alvoRef.current = indice;
    setAlvo(indice);
  }, []);

  const aoPressionar = (evento, indice) => {
    // Só botão principal do mouse; o direito abre menu de contexto e o do meio
    // cola no Linux.
    if (evento.button !== undefined && evento.button !== 0) return;

    gesto.current = {
      indice,
      x0: evento.clientX,
      y0: evento.clientY,
      // Posição corrente do ponteiro, mantida em dia pelo `pointermove`. O laço
      // de rolagem lê daqui, porque precisa continuar trabalhando com o mouse
      // parado na beirada — sem evento novo nenhum.
      x: evento.clientX,
      y: evento.clientY,
      scroll0: 0,
      levantou: false
    };
  };

  useEffect(() => {
    // O card arrastado segue o ponteiro por escrita DIRETA no DOM, sem passar
    // pelo estado: um `setState` por movimento re-renderizaria os 40 cards a cada
    // quadro. Quem vai ao estado é só o `alvo`, que muda poucas vezes.
    //
    // A escala vai junto no mesmo `transform` porque a propriedade é uma só:
    // declarar `scale` no CSS da classe seria sobrescrito por esta linha a cada
    // movimento, e o card cresceria só até o primeiro pixel arrastado.
    //
    // A parcela do SCROLL não é detalhe: o card mora dentro da grade e rola com
    // ela, então sem somar o quanto ela rolou desde o início do gesto, o card
    // escaparia do ponteiro exatamente enquanto a rolagem automática trabalha.
    function acompanharPonteiro() {
      const atual = gesto.current;
      const grade = listaRef.current;
      const card = atual && cardsRef.current[atual.indice];
      if (!atual || !card || !grade) return;

      const dx = atual.x - atual.x0;
      const dy = atual.y - atual.y0 + (grade.scrollTop - atual.scroll0);
      card.style.transform = `translate(${dx}px, ${dy}px) scale(${ESCALA_NA_MAO})`;
    }

    // O alvo é achado nas posições ORIGINAIS dos cards, e em coordenadas do
    // CONTEÚDO da grade (com o scroll somado). Duas razões: os outros cards estão
    // deslocados por `transform`, e mirar na posição deslocada faria o destino
    // oscilar entre dois índices a cada pixel; e a grade rola durante o gesto, o
    // que invalidaria qualquer medida presa à viewport.
    function acharAlvo() {
      const atual = gesto.current;
      const grade = listaRef.current;
      if (!atual || !atual.rects || !grade) return;

      const base = grade.getBoundingClientRect();
      const destino = indiceMaisProximo(
        atual.rects,
        atual.x - base.left + grade.scrollLeft,
        atual.y - base.top + grade.scrollTop
      );
      if (destino >= 0) marcarAlvo(destino);
    }

    // Rola a grade enquanto o ponteiro estiver na faixa junto a uma das bordas.
    // Vive num `requestAnimationFrame` porque precisa continuar rolando com o
    // ponteiro PARADO na beirada — se dependesse de `pointermove`, o professor
    // teria de sacudir o mouse para a lista andar.
    function passoDeRolagem() {
      const atual = gesto.current;
      const grade = listaRef.current;
      if (!atual || !atual.levantou || !grade) {
        rolagem.current = null;
        return;
      }

      const base = grade.getBoundingClientRect();
      const velocidade = velocidadeDaRolagem(
        atual.y - base.top,
        base.bottom - atual.y,
        ZONA_DE_ROLAGEM,
        VELOCIDADE_DE_ROLAGEM
      );

      if (velocidade !== 0) {
        const antes = grade.scrollTop;
        grade.scrollTop += velocidade;
        // Só refaz as contas se a grade REALMENTE rolou: nas pontas o scrollTop
        // não muda, e aí nada se moveu para recalcular.
        if (grade.scrollTop !== antes) {
          acompanharPonteiro();
          acharAlvo();
        }
      }

      rolagem.current = requestAnimationFrame(passoDeRolagem);
    }

    // Os ouvintes ficam no DOCUMENTO, e não no card: o ponteiro sai da grade o
    // tempo todo durante um arrasto, e se o `pointerup` acontecer fora, um
    // ouvinte local nunca o veria — o card ficaria preso à mão para sempre.
    function aoMover(evento) {
      const atual = gesto.current;
      if (!atual) return;

      // A posição do ponteiro fica guardada porque o laço de rolagem precisa
      // dela mesmo quando nenhum evento novo chega (mouse parado na beirada).
      atual.x = evento.clientX;
      atual.y = evento.clientY;

      if (!atual.levantou) {
        if (
          Math.abs(atual.x - atual.x0) <= LIMIAR_DE_ARRASTO &&
          Math.abs(atual.y - atual.y0) <= LIMIAR_DE_ARRASTO
        )
          return;

        // A grade existe sempre que há gesto — ele nasce de um `pointerdown` num
        // card, que é filho dela. Uma guarda basta, e ela é de sanidade.
        const grade = listaRef.current;
        if (!grade) return;

        atual.levantou = true;
        atual.scroll0 = grade.scrollTop;

        // Medido UMA VEZ, e em coordenadas do CONTEÚDO da grade: ler
        // `getBoundingClientRect` a cada `pointermove` força o navegador a
        // recalcular layout 60 vezes por segundo, e guardar coordenadas de
        // viewport quebraria assim que a grade rolasse.
        const base = grade.getBoundingClientRect();
        const cards = Array.from(grade.querySelectorAll("[data-ct-passo]"));
        atual.rects = cards.map(card => {
          const r = card.getBoundingClientRect();
          return {
            left: r.left - base.left + grade.scrollLeft,
            top: r.top - base.top + grade.scrollTop,
            width: r.width,
            height: r.height
          };
        });
        // A mesma medida vai para o estado, porque quem desloca os OUTROS cards é
        // o render; o gesto guarda a sua cópia para não depender de re-render.
        setGeometria(atual.rects);
        setArrastando(atual.indice);

        if (!rolagem.current) {
          rolagem.current = requestAnimationFrame(passoDeRolagem);
        }
      }

      acompanharPonteiro();
      acharAlvo();
    }

    function aoSoltar() {
      const atual = gesto.current;
      if (!atual) return;

      const destino = alvoRef.current;
      // Sem `levantou` não houve arrasto: foi um clique, e quem trata é o
      // `onClick` do card. Reordenar aqui trocaria a ordem sem ninguém pedir.
      if (atual.levantou && destino !== null && destino !== atual.indice) {
        const { itens: atuais, onReordenar: avisar } = dados.current;
        avisar(mover(atuais, atual.indice, destino));
      }
      terminarGesto();
    }

    function aoCancelar() {
      // `pointercancel` é o navegador tomando o gesto de volta (o dedo virou
      // scroll, chegou uma chamada). Soltar o card onde estiver seria mover sem
      // intenção — melhor devolver ao lugar.
      terminarGesto();
    }

    document.addEventListener("pointermove", aoMover);
    document.addEventListener("pointerup", aoSoltar);
    document.addEventListener("pointercancel", aoCancelar);
    return () => {
      document.removeEventListener("pointermove", aoMover);
      document.removeEventListener("pointerup", aoSoltar);
      document.removeEventListener("pointercancel", aoCancelar);
    };
    // Uma vez só, na montagem: o que muda entre renders é lido dos refs acima.
  }, [marcarAlvo, terminarGesto]);

  /**
   * Para onde o card `indice` se desloca enquanto o arrasto está em curso.
   *
   * Os que estão entre a origem e o destino cedem uma posição, cada um indo para
   * o lugar do vizinho. O deslocamento sai das coordenadas medidas, e não de uma
   * largura de card: numa grade, o vizinho do último da linha está na LINHA
   * SEGUINTE, e só as coordenadas reais sabem disso.
   */
  const deslocamentoDe = indice => {
    if (arrastando === null || alvo === null || !geometria) return null;
    if (indice === arrastando) return null;

    let vizinho = null;
    if (alvo > arrastando && indice > arrastando && indice <= alvo) {
      vizinho = indice - 1;
    } else if (alvo < arrastando && indice >= alvo && indice < arrastando) {
      vizinho = indice + 1;
    }
    if (vizinho === null || !geometria[vizinho] || !geometria[indice])
      return null;

    return {
      x: geometria[vizinho].left - geometria[indice].left,
      y: geometria[vizinho].top - geometria[indice].top
    };
  };

  const andar = (indice, direcao) => {
    const nova = moverUmaCasa(itens, indice, direcao);
    if (nova !== itens) onReordenar(nova);
  };

  const aoTeclar = (evento, indice) => {
    // Ctrl+seta reordena; seta sozinha continua navegando entre os cards, que é
    // o que quem usa teclado espera de uma grade. Sem o modificador, mover o card
    // e mover o foco seriam a mesma tecla.
    //
    // É o ÚNICO caminho sem arrasto desde 06/08/2026, quando os botões de seta
    // saíram de cada card — eram dois controles por passo, e a 40 passos viravam
    // 80 botões numa tela que já tem muita coisa. Por isso a dica embaixo da
    // grade diz a combinação em vez de só dizer "arraste".
    if (!evento.ctrlKey) return;
    if (evento.key !== "ArrowLeft" && evento.key !== "ArrowRight") return;

    evento.preventDefault();
    andar(indice, evento.key === "ArrowLeft" ? -1 : 1);
  };

  if (itens.length === 0) {
    return (
      <div className="ct-percurso ct-percurso--vazio">
        Clique nos objetos acima para montar o caminho que o aluno vai
        percorrer.
      </div>
    );
  }

  // A ordem que a trilha TERÁ se o professor soltar agora. Durante o arrasto os
  // selos mostram essa numeração, e não a atual: sem isso o card na mão dizia "1"
  // parado na sexta posição, e o primeiro da fila dizia "2" — os cards cediam
  // lugar e os números desmentiam o movimento.
  //
  // `mover` é a mesma função que o `pointerup` vai chamar, então o que se vê
  // durante o gesto é literalmente o resultado dele. `indexOf` acha pela
  // referência do objeto, que é preservada.
  const ordemPrevista =
    arrastando !== null && alvo !== null && alvo !== arrastando
      ? mover(itens, arrastando, alvo)
      : null;

  return (
    <div className="ct-percurso">
      <div
        className={`ct-percurso__grade${
          arrastando !== null ? " ct-percurso__grade--arrastando" : ""
        }`}
        ref={listaRef}
      >
        {itens.map((item, indice) => {
          const tipo = tipoDe(item);
          const ehArrastado = arrastando === indice;
          const desloc = deslocamentoDe(indice);
          const numero = ordemPrevista
            ? ordemPrevista.indexOf(item) + 1
            : indice + 1;
          const anuncio = `Passo ${indice + 1} de ${itens.length}: ${
            item.list.name
          }. Use Ctrl com as setas para mudar a posição.`;

          return (
            <div
              key={`${item.type}-${item.list.id}`}
              data-ct-passo
              ref={no => {
                cardsRef.current[indice] = no;
              }}
              className={`ct-passo${ehArrastado ? " ct-passo--arrastado" : ""}${
                desloc ? " ct-passo--cedendo" : ""
              }`}
              style={
                desloc
                  ? { transform: `translate(${desloc.x}px, ${desloc.y}px)` }
                  : undefined
              }
              onPointerDown={evento => aoPressionar(evento, indice)}
            >
              <button
                type="button"
                className="ct-passo__abrir"
                onKeyDown={evento => aoTeclar(evento, indice)}
                onClick={() => onEditar(item.type, item.list.id, tipo)}
                title={item.list.name}
              >
                {/* O número é um selo SOBRE o ícone, no canto inferior direito
                    dele. Ancorado ao ícone e não ao card porque, solto no canto
                    do card, ou comia altura útil (dentro) ou caía em cima do ✕
                    do vizinho da diagonal (fora) — as duas versões foram
                    reprovadas na tela, em 06/08/2026. */}
                <span className="ct-passo__icone-caixa">
                  <span
                    className={`ct-passo__icone bg-${tipo.color || "warning"}`}
                  >
                    <i className={`fa fa-${tipo.icon || "file"}`} />
                  </span>
                  <span className="ct-passo__ordem" aria-hidden="true">
                    {numero}
                  </span>
                </span>
                {/* Sem `.text-ellipsis`: o nome é o que o professor usa para
                    reconhecer o passo, e cortá-lo em 82px é o defeito que esta
                    grade veio corrigir. Três linhas, e o resto no title. */}
                <span className="ct-passo__nome">{item.list.name}</span>
                <span className="ct-sr-only">{anuncio}</span>
              </button>

              <button
                type="button"
                className="ct-passo__remover"
                onClick={() => onRemover(item.type, item.list.id)}
                title="Remover este passo"
                aria-label={`Remover ${item.list.name} da trilha`}
              >
                <i className="fa fa-close" aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PercursoDaTrilha;
